import cn from 'classnames'
import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { ComposedReportFYearSelect } from 'legacy/lut/components/widgets/public/partner360/composedReport/components/filterArea/filterFields/composedReportFYearSelect/ComposedReportFYearSelect'
import { TRANSLATION_KEYS } from 'legacy/lut/components/widgets/public/partner360/composedReport/translations/TRANSLATION_KEYS'
import {
  changeAmplifyMembershipSelectValue,
  changeComposedReportFYearSelectValue,
  changeGeoSelectValue,
  changeIsAcknowledgedValue,
  changeManagerSelectValue,
  changePartnersType,
  changePbmSelectValue,
  changeProductsSelectValue,
  changeScopeSelectValue,
  changeSpecializationsSelectValue,
  clearScope,
  forceToggleGlobalsModal,
  selectAmplifyMembershipValue,
  selectGeoSelectValue,
  selectIsAcknowledgedValue,
  selectManagerSelectValue,
  selectPartnersType,
  selectPbmSelectValue,
  selectProductsSelectValue,
  selectScopeSelectValue,
  selectSpecializationsSelectValue,
} from 'legacy/lut/features/partner360/composedReport'
import { selectComposedReportFYear } from 'legacy/lut/features/partner360/composedReport/selectors/selectComposedReportFYear'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Checkbox } from 'core-components/library/checkbox'
import { GeoMapping, PbmMultiselect, ProductTree } from 'partner360/components/filters'

import { ScopeSelectKeys } from '../../../models'

import { AmplifyMembershipMultiselect } from './amplifyMembershipMultiselect/AmplifyMembershipMultiselect'
import { ManagerMiltiselect } from './managerMultiselect/ManagerMultiselect'
import { PartnersTypeSelect } from './partersTypeSelect/PartnersTypeSelect'
import { ScopeSelect } from './scopeSelect/ScopeSelect'
import { SpecializationsMultiselect } from './specializationsMultiselect/SpecializationsMultiselect'

import styles from './FilterFields.module.scss'

export const FilterFields = () => {
  const dispatch = useDispatch()
  const { currentFYear } = useSelector(selectJBPSettings)
  const composedReportFYear = useSelector(selectComposedReportFYear)
  const scopeSelectValue = useSelector(selectScopeSelectValue)
  const geoSelectValue = useSelector(selectGeoSelectValue)
  const managerSelectValue = useSelector(selectManagerSelectValue)
  const pbmSelectValue = useSelector(selectPbmSelectValue)
  const amplifyMembershipValue = useSelector(selectAmplifyMembershipValue)
  const partnersType = useSelector(selectPartnersType)
  const productsSelectValue = useSelector(selectProductsSelectValue)
  const specializations = useSelector(selectSpecializationsSelectValue)
  const isAcknowledged = useSelector(selectIsAcknowledgedValue)

  const handleChangeScopeSelectValue = useCallback(
    (value: string) => {
      dispatch(changeScopeSelectValue(value as ScopeSelectKeys))
      if (value === ScopeSelectKeys.ManualGlobal) {
        dispatch(forceToggleGlobalsModal(true))
      }

      if (value === ScopeSelectKeys.Manual || value === ScopeSelectKeys.ManualGlobal) {
        dispatch(clearScope())
      }
    },
    [dispatch]
  )

  const handleChangesCountriesFilter = useCallback(
    (displayedIds: string[]) => {
      dispatch(changeGeoSelectValue(displayedIds))
    },
    [dispatch]
  )

  const handleChangesProductsFilter = useCallback(
    (displayedIds: string[]) => {
      dispatch(changeProductsSelectValue(displayedIds))
    },
    [dispatch]
  )

  const handleChangeAmplifyMembershipValue = useCallback(
    (value: string[]) => {
      dispatch(changeAmplifyMembershipSelectValue(value))
    },
    [dispatch]
  )

  const handleChangePmbValue = useCallback(
    (value: string[]) => {
      dispatch(changePbmSelectValue(value))
    },
    [dispatch]
  )

  const handleChangeManagerValue = useCallback(
    (value: string[]) => {
      dispatch(changeManagerSelectValue(value))
    },
    [dispatch]
  )

  const handleChangeIsAcknowledged = useCallback(
    (value: boolean) => {
      dispatch(changeIsAcknowledgedValue(value))
    },
    [dispatch]
  )

  const handleChangeComposedReportFYear = useCallback(
    (value: string) => {
      dispatch(changeComposedReportFYearSelectValue(Number(value)))
      handleChangesProductsFilter([])
    },
    [dispatch, handleChangesProductsFilter]
  )

  const handleChangePartnersType = useCallback(
    (value: string) => {
      dispatch(changePartnersType(value as PartnerType))
      handleChangesProductsFilter([])
    },
    [dispatch, handleChangesProductsFilter]
  )

  const handleChangeSpecializations = useCallback(
    (value: string[]) => {
      dispatch(changeSpecializationsSelectValue(value))
      handleChangesProductsFilter([])
    },
    [dispatch, handleChangesProductsFilter]
  )

  useEffect(() => {
    if (!composedReportFYear) {
      dispatch(changeComposedReportFYearSelectValue(currentFYear))
    }
  }, [currentFYear, dispatch, composedReportFYear])

  return (
    <div>
      <div className={styles.row}>
        <ComposedReportFYearSelect
          value={composedReportFYear}
          onChange={handleChangeComposedReportFYear}
          className={styles.select}
        />
        <PartnersTypeSelect
          value={partnersType}
          onChange={handleChangePartnersType}
          className={styles.select}
        />
        <ProductTree
          partnerType={partnersType}
          fYear={composedReportFYear}
          values={productsSelectValue}
          className={styles.productTree}
          popupClassName={styles.productTreePopup}
          label={<Translation text={TRANSLATION_KEYS.productsField} />}
          onSelectChange={handleChangesProductsFilter}
        />
        <ScopeSelect
          className={cn(styles.select, {
            [styles.manualScope]:
              scopeSelectValue === ScopeSelectKeys.ManualGlobal ||
              scopeSelectValue === ScopeSelectKeys.Manual,
          })}
          value={scopeSelectValue}
          partnersType={partnersType}
          onChange={handleChangeScopeSelectValue}
        />
        <RenderWhen
          condition={
            scopeSelectValue !== ScopeSelectKeys.Manual &&
            scopeSelectValue !== ScopeSelectKeys.ManualGlobal
          }
        >
          <GeoMapping
            values={geoSelectValue}
            className={styles.geoMapping}
            popupClassName={styles.geoMappingPopup}
            label={<Translation text={TRANSLATION_KEYS.geoField} />}
            onSelectChange={handleChangesCountriesFilter}
          />

          <RenderWhen condition={partnersType === PartnerType.Commercial}>
            <AmplifyMembershipMultiselect
              className={styles.select}
              value={amplifyMembershipValue}
              onChange={handleChangeAmplifyMembershipValue}
            />
          </RenderWhen>
          <SpecializationsMultiselect
            className={styles.select}
            value={specializations}
            onChange={handleChangeSpecializations}
            partnersType={partnersType}
          />
          <PbmMultiselect
            className={styles.select}
            value={pbmSelectValue}
            onChange={handleChangePmbValue}
            partnersType={partnersType}
          />
          <ManagerMiltiselect
            className={styles.select}
            value={managerSelectValue}
            onChange={handleChangeManagerValue}
          />
          <Checkbox
            className={styles.isPowerElite}
            checked={isAcknowledged}
            onChange={handleChangeIsAcknowledged}
            label={<Translation text={TRANSLATION_KEYS.isAcknowledgedField} />}
          />
        </RenderWhen>
      </div>
    </div>
  )
}
